import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import { PALETTES } from '@/store/palettes'
import { returnAfterPractice } from '@/utils/practiceReturn'
import { enhancePaletteForHighContrast } from '@/utils/highContrastPalette'
import { getPacerUiColors } from '@/utils/pacerUiColors'

const INHALE = 4
const EXHALE = 6
const PACER_PALETTE_KEY = 'fitutogether-pacer-palette'

const easeInOut = (t: number) =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2

const mixColour = (c1: [number, number, number], c2: [number, number, number], t: number) => {
  const mix = (a: number, b: number) => Math.round(a + (b - a) * t)
  return `rgb(${mix(c1[0], c2[0])}, ${mix(c1[1], c2[1])}, ${mix(c1[2], c2[2])})`
}

const hexToRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
]

const TEAL: [number, number, number] = [46, 158, 138]
const BLUE: [number, number, number] = [58, 127, 193]

const paceToSPC = (p: number) => 2.5 - (p - 1) * (1.9 / 9)

interface RunRef {
  phase: 'inhale' | 'exhale'
  startTime: number | null
  spc: number
}

export default function BreathingPacerPage() {
  const navigate          = useNavigate()
  const [searchParams]    = useSearchParams()
  const returnTo          = searchParams.get('return') ?? '/before-we-begin'
  const { palette, highContrast } = useTheme()

  const [phase,      setPhase]      = useState<'inhale' | 'exhale'>('inhale')
  const [progress,   setProgress]   = useState(0)
  const [count,      setCount]      = useState(1)
  const [isRunning,  setIsRunning]  = useState(false)
  const [pace,       setPace]       = useState(5)
  const [photoSrc,      setPhotoSrc]      = useState<string | null>(null)
  const [isDragging,    setIsDragging]    = useState(false)
  const [pacerPaletteId, setPacerPaletteId] = useState<string | null>(
    () => localStorage.getItem(PACER_PALETTE_KEY)
  )

  const frameRef    = useRef<number | null>(null)
  const runRef      = useRef<RunRef>({ phase: 'inhale', startTime: null, spc: 1.2 })
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const spc = paceToSPC(pace)
  useEffect(() => { runRef.current.spc = spc }, [spc])

  // Load photo from IndexedDB on mount
  useEffect(() => {
    const req = indexedDB.open('fitutogether', 1)
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains('pacer')) db.createObjectStore('pacer')
    }
    req.onsuccess = (e) => {
      const db = (e.target as IDBOpenDBRequest).result
      const get = db.transaction('pacer', 'readonly').objectStore('pacer').get('photo')
      get.onsuccess = () => { if (get.result) setPhotoSrc(get.result as string) }
    }
  }, [])

  useEffect(() => {
    if (!isRunning) {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      setProgress(0); setPhase('inhale'); setCount(1)
      runRef.current.startTime = null
      return
    }
    runRef.current.phase     = 'inhale'
    runRef.current.startTime = null

    const animate = (ts: number) => {
      const r = runRef.current
      if (!r.startTime) r.startTime = ts
      const counts   = r.phase === 'inhale' ? INHALE : EXHALE
      const duration = counts * r.spc * 1000
      const elapsed  = ts - r.startTime
      const prog     = Math.min(elapsed / duration, 1)

      // Exhale counts DOWN (6 → 1) so the phase transition reads 4 → 6, not 4 → 1
      const c = r.phase === 'inhale'
        ? Math.min(Math.floor(prog * INHALE) + 1, INHALE)
        : Math.max(1, EXHALE - Math.floor(prog * EXHALE))

      setPhase(r.phase)
      setProgress(prog)
      setCount(c)

      if (prog >= 1) {
        // Carry over any overrun so the new phase starts at the right moment,
        // not snapped back to zero — eliminates the 1-frame timing glitch
        const excess = elapsed - duration
        r.phase     = r.phase === 'inhale' ? 'exhale' : 'inhale'
        r.startTime = ts - excess
      }
      frameRef.current = requestAnimationFrame(animate)
    }

    frameRef.current = requestAnimationFrame(animate)
    return () => { if (frameRef.current !== null) cancelAnimationFrame(frameRef.current) }
  }, [isRunning])

  const handlePaletteSelect = (id: string) => {
    if (pacerPaletteId === id) {
      setPacerPaletteId(null)
      localStorage.removeItem(PACER_PALETTE_KEY)
    } else {
      setPacerPaletteId(id)
      localStorage.setItem(PACER_PALETTE_KEY, id)
    }
  }

  const removePhoto = () => {
    setPhotoSrc(null)
    const req = indexedDB.open('fitutogether', 1)
    req.onsuccess = (e) => {
      const db = (e.target as IDBOpenDBRequest).result
      db.transaction('pacer', 'readwrite').objectStore('pacer').delete('photo')
    }
  }

  const handleFile = (file: File | null | undefined) => {
    if (!file?.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = (e.target as FileReader).result as string
      setPhotoSrc(dataUrl)
      const req = indexedDB.open('fitutogether', 1)
      req.onsuccess = (ev) => {
        const db = (ev.target as IDBOpenDBRequest).result
        db.transaction('pacer', 'readwrite').objectStore('pacer').put(dataUrl, 'photo')
      }
    }
    reader.readAsDataURL(file)
  }

  const eased      = easeInOut(progress)
  const isInhale   = phase === 'inhale'
  const scale      = isInhale ? 0.5 + eased * 0.5 : 1.0 - eased * 0.5

  const selectedPacer = pacerPaletteId ? PALETTES.find(p => p.id === pacerPaletteId) : null
  const activePacer = selectedPacer
    ? (highContrast ? enhancePaletteForHighContrast(selectedPacer) : selectedPacer)
    : null
  const C1 = activePacer ? hexToRgb(activePacer.ring)   : TEAL
  const C2 = activePacer ? hexToRgb(activePacer.swatch) : BLUE
  const colour = isRunning ? mixColour(C1, C2, isInhale ? eased : 1 - eased) : '#ccc'
  const totalCounts = isInhale ? INHALE : EXHALE
  const inhaleTime  = (INHALE * spc).toFixed(1)
  const exhaleTime  = (EXHALE * spc).toFixed(1)

  const ui = getPacerUiColors(!!photoSrc, highContrast, palette)

  return (
    /* Full-viewport shell — clips everything, prevents any page scroll */
    <div style={{
      width: '100vw', height: '100dvh',
      overflow: 'hidden', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: ui.shellBg,
      transition: 'background 0.35s ease',
    }}>
    {/* Inner content pane — capped at iPhone SE height so nothing overflows on taller devices either */}
    <div style={{
      width: '100%', maxWidth: 390,
      height: '100%', maxHeight: 667,
      margin: '0 auto', fontFamily: "'Lexend', sans-serif",
      position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: ui.paneBg, borderRadius: 24,
      border: photoSrc ? 'none' : ui.paneBorder,
      transition: 'border-color 0.4s ease, background 0.35s ease',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@200;300;400&display=swap');
        .bp-root * { box-sizing: border-box; margin: 0; padding: 0; text-shadow: ${ui.textShadow}; }
        .bp-root input[type=range] {
          -webkit-appearance: none;
          width: 100%; height: 2px;
          background: ${ui.rangeTrack}; border-radius: 1px;
          outline: none; cursor: pointer;
        }
        .bp-root input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px; height: 14px; border-radius: 50%;
          background: ${ui.rangeThumb}; cursor: pointer; transition: transform 0.15s;
        }
        .bp-root input[type=range]:hover::-webkit-slider-thumb { transform: scale(1.2); }
      `}</style>

      {/* Photo background */}
      {photoSrc && (
        <img src={photoSrc} alt="" style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%', objectFit: 'cover',
        }} />
      )}

      {/* Light overlay — only when a photo is present */}
      <div style={{
        position: 'absolute', inset: 0,
        background: ui.overlay,
        transition: 'background 0.35s ease',
      }} />

      {/* No-photo state — tap to add */}
      {!photoSrc && (
        <div
          onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files?.[0]) }}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => fileInputRef.current?.click()}
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'flex-end',
            paddingBottom: 32, gap: 8, cursor: 'pointer',
          }}
        >
          <div style={{
            border: `1px dashed ${isDragging ? ui.addPhotoBorderActive : ui.addPhotoBorder}`,
            borderRadius: 8, padding: '10px 24px',
            fontSize: 11, letterSpacing: '0.1em',
            color: ui.addPhoto, textTransform: 'uppercase',
          }}>
            add a photo
          </div>
        </div>
      )}
      <input ref={fileInputRef} type="file" accept="image/*"
        onChange={(e) => handleFile(e.target.files?.[0])}
        style={{ display: 'none' }} />

      {/* Main UI */}
      <div className="bp-root" style={{
        position: 'relative', zIndex: 10,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 24,
      }}>
        {/* Phase label */}
        <div style={{
          fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase',
          fontWeight: 300,
          color: isRunning ? ui.phaseRunning : ui.phaseIdle,
          transition: 'color 0.6s ease, background 0.35s ease',
          background: ui.chipBg, borderRadius: 20, padding: '4px 12px',
        }}>
          {isRunning ? (isInhale ? 'inhale' : 'exhale') : 'ready'}
        </div>

        {/* Circle + ripple rings */}
        <div style={{
          position: 'relative', width: 180, height: 180,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isRunning && ([1.5, 2.0, 2.6] as const).map((multiplier, i) => (
            <div key={i} style={{
              position: 'absolute', width: 110, height: 110,
              borderRadius: '50%', border: `1px solid ${colour}`,
              transform: `scale(${scale * multiplier})`,
              opacity: Math.max(0, 0.4 - i * 0.12),
              transition: 'opacity 0.1s ease', pointerEvents: 'none',
            }} />
          ))}

          <div style={{
            width: 110, height: 110, borderRadius: '50%',
            background: isRunning ? colour : ui.circleIdleBg,
            transform: `scale(${isRunning ? scale : 0.6})`,
            transition: 'background 0.6s ease',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 2,
            boxShadow: isRunning ? `0 0 40px ${colour}55` : 'none',
          }}>
            <div style={{
              fontSize: 42, fontWeight: 200, color: isRunning ? '#ffffff' : ui.countColor,
              lineHeight: 1, letterSpacing: '-0.02em',
            }}>
              {isRunning ? count : '·'}
            </div>
            {isRunning && (
              <div style={{
                fontSize: 9, color: ui.countSubColor,
                letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
                of {totalCounts}
              </div>
            )}
          </div>
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', gap: 8, height: 12 }}>
          {Array.from({ length: totalCounts }).map((_, i) => (
            <div key={`${phase}-${i}`} style={{
              width: 8, height: 8, borderRadius: '50%',
              background: isRunning && i < count ? colour : ui.dotInactive,
              transition: 'background 0.1s ease',
            }} />
          ))}
        </div>

        {/* Begin / Pause */}
        <button
          onClick={() => setIsRunning(r => !r)}
          style={{
            background: ui.chipBg,
            border: `1px solid ${isRunning ? ui.beginRunningBorder : ui.beginBorder}`,
            color: isRunning ? ui.beginRunningColor : ui.beginColor,
            padding: '11px 44px', borderRadius: 20,
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            cursor: 'pointer', fontFamily: "'Lexend', sans-serif", fontWeight: 300,
            transition: 'all 0.3s ease',
          }}
        >
          {isRunning ? 'pause' : 'begin'}
        </button>

        {/* Done */}
        <button
          onClick={() => navigate(returnAfterPractice(returnTo))}
          style={{
            background: ui.chipBg, border: 'none', borderRadius: 20,
            color: ui.doneColor, fontSize: 11,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            cursor: 'pointer', fontFamily: "'Lexend', sans-serif", fontWeight: 300,
            padding: '4px 12px',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = ui.doneHover }}
          onMouseLeave={e => { e.currentTarget.style.color = ui.doneColor }}
        >
          done
        </button>

        {/* Pace slider */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: 220,
          background: ui.panelBg, borderRadius: 20, padding: '8px 16px',
          transition: 'background 0.35s ease',
        }}>
          <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: ui.labelMuted, fontWeight: 300 }}>
            pace
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
            <span style={{ fontSize: 9, color: ui.sliderEnds, letterSpacing: '0.08em', flexShrink: 0 }}>slow</span>
            <input type="range" min={1} max={10} step={1} value={pace}
              onChange={(e) => setPace(Number(e.target.value))} />
            <span style={{ fontSize: 9, color: ui.sliderEnds, letterSpacing: '0.08em', flexShrink: 0 }}>fast</span>
          </div>
          <div style={{ fontSize: 10, color: ui.timing, letterSpacing: '0.04em', fontWeight: 300 }}>
            {inhaleTime}s inhale · {exhaleTime}s exhale
          </div>
        </div>
      </div>

      {/* Palette swatches — bottom left */}
      <div style={{
        position: 'absolute', bottom: 16, left: 16, zIndex: 10,
        display: 'flex', flexDirection: 'column', gap: 6,
        alignItems: 'center',
      }}>
        {PALETTES.map(p => {
          const isSelected = pacerPaletteId === p.id
          return (
            <button
              key={p.id}
              onClick={() => handlePaletteSelect(p.id)}
              title={p.label}
              aria-label={`${p.label}${isSelected ? ' — selected' : ''}`}
              aria-pressed={isSelected}
              style={{
                width: 20, height: 20, borderRadius: '50%', padding: 0,
                background: p.swatch,
                border: `1.5px solid ${isSelected ? p.ring : 'rgba(0,0,0,0.12)'}`,
                cursor: 'pointer', outline: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: isSelected ? `0 0 0 1.5px ${p.ring}` : 'none',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                fontSize: 9, lineHeight: 1, color: p.text, fontFamily: "'Lexend', sans-serif",
              }}
            >
              {isSelected ? '✓' : ''}
            </button>
          )
        })}
      </div>

      {/* Brand mark */}
      <div style={{
        position: 'absolute', bottom: 20, left: 0, right: 0,
        textAlign: 'center', zIndex: 10, fontSize: 9,
        letterSpacing: '0.16em', color: ui.brand,
        textTransform: 'uppercase', fontFamily: "'Lexend', sans-serif", fontWeight: 300,
      }}>
        FitUTogether
      </div>

      {/* Photo controls — change / remove */}
      {photoSrc && (
        <div style={{
          position: 'absolute', top: 16, right: 16, zIndex: 10,
          display: 'flex', gap: 6,
        }}>
          <button onClick={() => fileInputRef.current?.click()} style={{
            background: ui.photoBtnBg, border: `1px solid ${ui.photoBtnBorder}`,
            borderRadius: 4, color: ui.photoBtnText, fontSize: 10,
            letterSpacing: '0.1em', padding: '6px 10px', cursor: 'pointer',
            fontFamily: "'Lexend', sans-serif",
          }}>
            change photo
          </button>
          <button onClick={removePhoto} style={{
            background: ui.photoBtnBg, border: `1px solid ${ui.photoBtnBorder}`,
            borderRadius: 4, color: ui.photoBtnText, fontSize: 10,
            letterSpacing: '0.1em', padding: '6px 10px', cursor: 'pointer',
            fontFamily: "'Lexend', sans-serif",
          }}>
            remove
          </button>
        </div>
      )}
    </div>
    </div>
  )
}
